'use client'

import { searchStocks, type Stock } from '@/app/actions/search/search-stocks'
import { cn } from '@/lib/utils'
import debounce from 'lodash.debounce'
import { useRouter } from 'next/navigation'
import { useEffect, useMemo, useState, useTransition } from 'react'
import styles from './styles/StockSearch.module.css'

export function StockSearch() {
    const [query, setQuery] = useState('')
    const [results, setResults] = useState<Stock[]>([])
    const [isPending, startTransition] = useTransition()
    const [isFocused, setIsFocused] = useState(false)
    const router = useRouter()

    const debouncedSearch = useMemo(
        () =>
            debounce((value: string) => {
                startTransition(async () => {
                    const data = await searchStocks(value)
                    setResults(data)
                })
            }, 300),
        [startTransition]
    )

    useEffect(() => {
        return () => {
            debouncedSearch.cancel()
        }
    }, [debouncedSearch])

    return (
        <div className={styles.container}>
            <input
                type="search"
                placeholder="Search ticker or company..."
                value={query}
                className={cn(styles.searchInput)}
                onFocus={() => setIsFocused(true)}
                onBlur={() => {
                    setTimeout(() => {
                        setIsFocused(false)
                        setQuery('') // Clear text when clicked away
                        setResults([]) // Clear results when clicked away
                    }, 200)
                }}
                onChange={(e) => {
                    setQuery(e.target.value)
                    debouncedSearch(e.target.value)
                }}
            />

            {query.length > 1 && isFocused && (
                <div className={styles.searchResults}>
                    {isPending ? (
                        <div className={styles.loadingState}>Searching...</div>
                    ) : results.length === 0 ? (
                        <div className={styles.emptyState}>No results found</div>
                    ) : (
                        <ul className={styles.resultsList} role="listbox">
                            {results.map((stock) => (
                                <li
                                    key={stock.t}
                                    role="option"
                                    aria-selected="false"
                                    className={styles.resultItem}
                                    onClick={() => {
                                        router.push(`/stock/${encodeURIComponent(stock.t)}`)
                                        setQuery('')
                                        setResults([])
                                    }}
                                >
                                    <span className={styles.stockInfo}>
                                        <b>{stock.n1}</b>
                                        <span className={styles.stockSymbol}>{` (${stock.t})`}</span>
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </div>
    )
}